import React, { useState, useEffect } from 'react';
import { Segmented, Select, Empty } from 'antd';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import type { LLMGroupResponseType } from 'state';
import type { Studio2Player } from '../../live/types';

type Props = {
  player: Studio2Player;
  groupReports: LLMGroupResponseType[];
  // Cohort (trainer) view shows a "Player report" / "Cohort summary" toggle.
  // The per-player (self) view hides it entirely — a trainee only ever sees
  // their own report, never the cohort summary.
  showGroup?: boolean;
};

const ReportPanel: React.FC<Props> = ({
  player,
  groupReports,
  showGroup = true,
}) => {
  const [scope, setScope] = useState<'player' | 'group'>('player');
  const [promptIdx, setPromptIdx] = useState(0);
  // Group reports are session-level (not per player), so this selection
  // persists as the trainer moves between players.
  const [groupIdx, setGroupIdx] = useState(0);

  useEffect(() => {
    setPromptIdx(0);
    setScope('player');
  }, [player.id]);

  const reports = player.ai_reports;
  const current = reports[Math.min(promptIdx, reports.length - 1)];

  const gi = groupReports.length
    ? Math.min(groupIdx, groupReports.length - 1)
    : 0;
  const currentGroup = groupReports[gi];

  const activeScope = showGroup ? scope : 'player';
  const markdown =
    activeScope === 'group'
      ? (currentGroup?.response ?? '')
      : (current?.response.response ?? '');

  return (
    <div className="studio2__report">
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 12,
          flexWrap: 'wrap',
        }}
      >
        {showGroup && (
          <Segmented
            value={scope}
            onChange={v => setScope(v as 'player' | 'group')}
            options={[
              { label: 'Player report', value: 'player' },
              { label: 'Cohort summary', value: 'group' },
            ]}
          />
        )}
        {activeScope === 'player' ? (
          // Only show the picker when reports exist — otherwise the Select's
          // value falls back to reports.length - 1 = -1 and renders "-1".
          reports.length > 0 && (
            <Select
              size="small"
              value={Math.min(promptIdx, reports.length - 1)}
              onChange={setPromptIdx}
              style={{ minWidth: 240 }}
              options={reports.map((r, i) => ({
                label: r.prompt.name,
                value: i,
              }))}
            />
          )
        ) : (
          <>
            {groupReports.length > 1 && (
              <Select
                size="small"
                value={gi}
                onChange={setGroupIdx}
                style={{ minWidth: 240 }}
                options={groupReports.map((g, i) => ({
                  label: g.group_report_name,
                  value: i,
                }))}
              />
            )}
            {currentGroup && (
              <span className="s2-muted" style={{ fontSize: 12 }}>
                {groupReports.length > 1
                  ? `${currentGroup.player_ids.length} players`
                  : `${currentGroup.group_report_name} · ${currentGroup.player_ids.length} players`}
              </span>
            )}
          </>
        )}
      </div>

      <div className="studio2__card">
        <div className="studio2__card-body studio2__card-body--scroll">
          {markdown.trim() ? (
            <div className="studio2__markdown">
              <ReactMarkdown remarkPlugins={[remarkGfm]}>
                {markdown}
              </ReactMarkdown>
            </div>
          ) : (
            <div
              style={{
                height: '100%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <Empty description="Reports will be available at the end of the session." />
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ReportPanel;
